/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * Mute/unmute control for autoplaying videos in a scoped root.
 * Starts muted (autoplay policy); unmute requires a user gesture.
 */
class VideoSoundControl extends HTMLElement {
  /** @type {boolean} */
  #muted = true;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    // Crossing the breakpoint swaps which video is displayed.
    this.#breakpoint.addEventListener('change', this.#onBreakpointChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#breakpoint.removeEventListener('change', this.#onBreakpointChange);
  }

  #breakpoint = window.matchMedia('(min-width: 750px)');

  #onBreakpointChange = () => {
    this.#sync();
  };

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-video-sound-button]')) return;

    event.preventDefault();
    event.stopPropagation();

    this.#muted = !this.#muted;
    this.#sync();
  };

  #sync() {
    const button = this.querySelector('[data-video-sound-button]');

    button?.setAttribute('aria-pressed', String(!this.#muted));
    button?.setAttribute(
      'aria-label',
      this.#muted ? this.dataset.unmuteLabel || 'Unmute video' : this.dataset.muteLabel || 'Mute video',
    );

    const root = this.#root;

    for (const video of root.querySelectorAll('video')) {
      // A hero carries a desktop and a mobile video and hides one per
      // breakpoint; only the displayed one may sound, or both play at once.
      if (!this.#isDisplayed(video)) {
        video.muted = true;
        continue;
      }

      video.muted = this.#muted;

      if (!this.#muted) video.play().catch(() => {});
    }

    for (const iframe of root.querySelectorAll('iframe')) {
      this.#muteIframe(iframe);
    }
  }

  /**
   * @param {HTMLVideoElement} video
   * @returns {boolean}
   */
  #isDisplayed(video) {
    return video.getClientRects().length > 0;
  }

  /** @returns {Element} */
  get #root() {
    const selector = this.dataset.root;

    if (selector) return this.closest(selector) || this.parentElement || this;

    return this.parentElement || this;
  }

  /**
   * @param {HTMLIFrameElement} iframe
   */
  #muteIframe(iframe) {
    const win = iframe.contentWindow;
    if (!win) return;

    const src = iframe.src || '';
    const type = iframe.dataset.videoType;

    if (type === 'youtube' || src.includes('youtube.com')) {
      win.postMessage(JSON.stringify({ event: 'command', func: this.#muted ? 'mute' : 'unMute', args: [] }), '*');
      return;
    }

    if (type === 'vimeo' || src.includes('vimeo.com')) {
      win.postMessage(JSON.stringify({ method: 'setVolume', value: this.#muted ? 0 : 1 }), '*');
    }
  }
}

if (!customElements.get('video-sound-control')) {
  customElements.define('video-sound-control', VideoSoundControl);
}
